import { useEffect, useMemo, useRef, useState } from 'react'
import { useUsersAndGroups } from '@opallabs/api'
import {
  type MenuOption,
  type NotificationMode,
  pendingNotificationIds,
} from '@opallabs/concepts'
import { Spacing } from '@opallabs/gem'
import { MultiSelectDiffObject } from '@opallabs/hooks'
import { MultiUserSelect, AvatarList } from '@opallabs/lapidary'
import { NotificationFooter, PendingBadge } from '../../../Notifications'
import { ConfirmNotificationsOffModal } from '../../../Notifications/ConfirmNotificationsOffModal'
import { FieldUsersSelectValue } from '../../../types'
import type { FieldNotificationHandlers, UsersSelectField } from '../../types'
import { useSwappableField } from '../../useSwappableField'
import { TextDisplay } from '../TextDisplay'
import * as styles from './styles'

const ids = (options: MenuOption[]) => options.map((option) => option.value)

const toUsersSelectValue = (options: MenuOption[]): FieldUsersSelectValue => ({
  user_ids: options
    .filter((option) => option.resourceType !== 'user_group')
    .map((option) => option.value),
  group_ids: options
    .filter((option) => option.resourceType === 'user_group')
    .map((option) => option.value),
})

type FieldInputProps<T> = {
  field: T
  readOnly?: boolean
  notifications?: FieldNotificationHandlers
  onChange: (change: {
    fieldId: string
    data?: {
      id?: string
      value: FieldUsersSelectValue
    }
  }) => void
  setIsEditing: (isEditing: boolean) => void
}

export const FieldUsersSelect = <T extends UsersSelectField>({
  field,
  readOnly = false,
  notifications,
  onChange,
  setIsEditing,
}: FieldInputProps<T>) => {
  const selectRef = useRef<HTMLButtonElement>(null)
  const { setDisplayRef, onStopEditing } = useSwappableField({ setIsEditing })
  const { data: usersAndGroups, isLoading } = useUsersAndGroups()

  const [isOpen, setIsOpen] = useState(false)

  const options = useMemo(() => {
    if (!usersAndGroups) return []

    const selectedUserIds = field.data?.value?.user_ids || []
    const selectedGroupIds = field.data?.value?.group_ids || []
    const selectedIds = [...selectedUserIds, ...selectedGroupIds]

    return usersAndGroups
      .filter((u) => u.isActive || selectedIds.includes(u.id))
      .map(
        (item) =>
          ({
            value: item.id,
            label: item.label,
            avatarUrl: item.avatarUrl,
            resourceType: item.resource_type,
            disabled: !item.isActive,
          }) satisfies MenuOption
      )
  }, [
    usersAndGroups,
    field.data?.value?.user_ids,
    field.data?.value?.group_ids,
  ])

  const storedIds = useMemo(
    () => [
      ...(field.data?.value?.user_ids || []),
      ...(field.data?.value?.group_ids || []),
    ],
    [field.data?.value?.user_ids, field.data?.value?.group_ids]
  )

  const selectedValues = useMemo(() => {
    if (!storedIds.length || !options) return []

    return storedIds.reduce<MenuOption[]>((acc, id) => {
      const option = options.find((option) => option.value === id)
      if (option) acc.push(option)
      return acc
    }, [])
  }, [storedIds, options])

  const previouslyPending = useMemo(() => {
    const targets = field.data?.pending_notification_targets
    return targets
      ? { userIds: targets.user_ids, groupIds: targets.group_ids }
      : undefined
  }, [field.data?.pending_notification_targets])

  // A value row copies the field's default mode when it is created, so until
  // one exists the default is the mode a change would start from.
  const mode = field.data?.notification_mode ?? field.defaultNotificationMode

  const [isConfirmingOff, setIsConfirmingOff] = useState(false)
  const focusPickerSearchRef = useRef<() => void>()
  const changeMode = (nextMode: NotificationMode) =>
    notifications?.onModeChange({ fieldId: field.id, mode: nextMode })

  const pendingIds = (liveIds: string[]) =>
    pendingNotificationIds({
      liveIds,
      mode,
      previouslyPending,
      storedIds,
    })
  const pendingCount = (liveIds: string[]) => pendingIds(liveIds).size

  // An inherited value's row belongs to an ancestor block, so its mode and its
  // pending set are not this block's to change.
  const hasNotifications = Boolean(notifications) && !field.isCascadedValue
  const badgeCount =
    !isOpen && hasNotifications && !isLoading ? pendingCount(storedIds) : 0

  const handleSelectionChange = (
    changeSet: MultiSelectDiffObject<MenuOption>
  ) => {
    const getSortedIds = (userOptions: MenuOption[]) =>
      userOptions
        .map((option) => option.value)
        .sort()
        .join(',')

    if (getSortedIds(selectedValues) === getSortedIds(changeSet.current)) {
      return
    }

    onChange({
      fieldId: field.id,
      data: {
        id: field.data?.id || undefined,
        value: toUsersSelectValue(changeSet.current),
      },
    })
  }

  useEffect(() => {
    setDisplayRef(!readOnly ? selectRef.current : null)
  }, [readOnly, setDisplayRef])

  if (readOnly) {
    if (selectedValues?.length) {
      return (
        <div css={styles.readOnlyAvatarList}>
          <AvatarList avatarSize={Spacing.L} items={selectedValues} />
        </div>
      )
    }
    return (
      <TextDisplay
        fieldId={field.id}
        readOnly={readOnly}
        fieldLabel="None"
        setIsEditing={setIsEditing}
      />
    )
  }

  return (
    <>
      <MultiUserSelect
        css={styles.selectOverrides}
        customFooter={
          notifications && mode && !field.isCascadedValue
            ? ({ focusSearch, selectedItems }) => {
                const pending = pendingCount(ids(selectedItems))
                return (
                  <NotificationFooter
                    isNotifyDisabled={isLoading}
                    mode={mode}
                    onModeChange={(nextMode) => {
                      if (nextMode === 'off' && pending > 0) {
                        focusPickerSearchRef.current = focusSearch
                        setIsConfirmingOff(true)
                      } else {
                        changeMode(nextMode)
                      }
                    }}
                    onNotify={() =>
                      notifications.onNotify({
                        fieldId: field.id,
                        value: toUsersSelectValue(selectedItems),
                      })
                    }
                    pendingCount={pending}
                    selectedIds={ids(selectedItems)}
                  />
                )
              }
            : undefined
        }
        externalRef={selectRef}
        htmlId={`field-${field.id}`}
        initialSelection={selectedValues}
        onMenuCloseDiff={handleSelectionChange}
        onOpenChange={(open) => {
          setIsOpen(open)
          if (open) {
            setIsEditing(true)
          } else {
            onStopEditing()
            setTimeout(() => {
              selectRef.current?.focus()
            }, 50)
          }
        }}
        options={options}
        pendingValues={hasNotifications ? pendingIds(ids(options)) : undefined}
        placeholder="None"
        showTriggerIcon={true}
        sortSelectionList={true}
        trailingNode={
          // The open menu's footer already reports the count, so a badge on the
          // control would only crowd it.
          badgeCount > 0 ? <PendingBadge count={badgeCount} /> : undefined
        }
      />

      {isConfirmingOff && (
        <ConfirmNotificationsOffModal
          onCancel={() => setIsConfirmingOff(false)}
          onCloseAutoFocus={() => focusPickerSearchRef.current?.()}
          onConfirm={() => {
            setIsConfirmingOff(false)
            changeMode('off')
          }}
        />
      )}
    </>
  )
}
